// api
import type { PortfolioAnalysisOccupancyOutput } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisMeters';

// components
import type {
  OccupancyDay,
  PortfolioAnalysisOccupancy,
} from '@/features/energyxplorer/types';

// relative
import type {
  DayKey,
  EditMeterOccupancyFormValues,
} from './editMeterOccupancyForm.schema';
import { DAYS_OF_WEEK } from './editMeterOccupancyForm.schema';

const GRANULARITY = 4;
const DEFAULT_START = 8 * GRANULARITY;
const DEFAULT_END = 16 * GRANULARITY;

function resolveOccupancy(
  data: PortfolioAnalysisOccupancyOutput,
): PortfolioAnalysisOccupancy {
  return (
    data.ManualOccupancy ?? data.CalculatedOccupancy ?? data.DefaultOccupancy
  );
}

function dayToFormValues(day: OccupancyDay): {
  active: boolean;
  start: number;
  end: number;
} {
  if (!day.occupancy) {
    return { active: false, start: DEFAULT_START, end: DEFAULT_END };
  }
  return {
    active: true,
    start:
      typeof day.start_time === 'number'
        ? day.start_time * GRANULARITY
        : DEFAULT_START,
    end:
      typeof day.end_time === 'number'
        ? day.end_time * GRANULARITY
        : DEFAULT_END,
  };
}

export function toEditMeterOccupancyFormValues(
  data: PortfolioAnalysisOccupancyOutput,
): EditMeterOccupancyFormValues {
  const occupancy = resolveOccupancy(data);
  return Object.fromEntries(
    DAYS_OF_WEEK.map((day) => [day, dayToFormValues(occupancy[day])]),
  ) as EditMeterOccupancyFormValues;
}

export function toCalculatedMeterOccupancyFormValues(
  data: PortfolioAnalysisOccupancyOutput,
): EditMeterOccupancyFormValues {
  const occupancy = data.CalculatedOccupancy ?? data.DefaultOccupancy;
  return Object.fromEntries(
    DAYS_OF_WEEK.map((day) => [day, dayToFormValues(occupancy[day])]),
  ) as EditMeterOccupancyFormValues;
}

export function toPortfolioAnalysisOccupancy(
  values: EditMeterOccupancyFormValues,
): PortfolioAnalysisOccupancy {
  return Object.fromEntries(
    DAYS_OF_WEEK.map((day: DayKey) => {
      const { active, start, end } = values[day];
      if (!active) {
        return [
          day,
          { occupancy: false, start_time: undefined, end_time: undefined },
        ];
      }
      return [
        day,
        {
          occupancy: true,
          start_time: start / GRANULARITY,
          end_time: end / GRANULARITY,
        },
      ];
    }),
  ) as PortfolioAnalysisOccupancy;
}
